/**
 * Paramétrage des moteurs de transcription (Copilot temps réel).
 *
 * L'écran porte aussi le test de la **génération** de suggestions. Les deux
 * moitiés du copilote tombent en panne séparément — il transcrit très bien
 * sans suggérer une seule fois — et l'appel n'est pas le moment de le
 * découvrir : la panne y est silencieuse et le prospect attend.
 *
 * L'écran est **généré** à partir des `config_fields` renvoyés par l'API : le
 * front ne code aucun fournisseur en dur, exactement comme pour les
 * fournisseurs téléphoniques. Ajouter Whisper côté backend le fera apparaître
 * ici sans toucher à ce fichier.
 *
 * Les clés API ne redescendent jamais en clair : le champ affiche un masque, et
 * un enregistrement qui ne le modifie pas laisse la clé stockée intacte.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, PlugZap, CheckCircle2, XCircle, Loader2, Mic, AlertTriangle, HelpCircle,
  Sparkles,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import { CARD, DOT, PILL, inputCls, btnPrimary, btnGhost } from "@/crm/components/telephony/shared";

const CHECK_LABELS = {
  connectivity: "Connectivité",
  credentials: "Clé API",
  model: "Modèle",
  generation: "Génération",
};

const FIELD_LABEL = "block text-xs text-slate-500";

export default function TranscriptionSettings() {
  const [providers, setProviders] = useState([]);
  const [active, setActive] = useState("");
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  // Test de la génération : indépendant du moteur de transcription sélectionné,
  // c'est l'autre moitié du copilote.
  const [llmTesting, setLlmTesting] = useState(false);
  const [llmResult, setLlmResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/copilot/stt/providers");
      setProviders(data.providers || []);
      setActive(data.active || "");
      const first = data.active || data.providers?.[0]?.type || "";
      setSelected(first);
      const target = (data.providers || []).find((p) => p.type === first);
      setForm(target?.configuration || {});
      setDirty(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const provider = providers.find((p) => p.type === selected);

  const pick = (type) => {
    setSelected(type);
    setForm(providers.find((p) => p.type === type)?.configuration || {});
    setResult(null);
    setDirty(false);
  };

  const setField = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/copilot/stt/providers/${selected}/config`, form);
      setForm(data);
      setProviders((list) => list.map((p) => (p.type === selected ? { ...p, configuration: data } : p)));
      setDirty(false);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post(`/copilot/stt/providers/${selected}/test`);
      setResult(data);
      if (data.ok) toast.success("Connexion établie");
      else toast.error(data.detail || "Test échoué");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const testLlm = async () => {
    setLlmTesting(true); setLlmResult(null);
    try {
      const { data } = await api.post("/copilot/llm/test");
      setLlmResult(data);
      if (data.ok) toast.success("Le copilote a produit une suggestion");
      else toast.error(data.detail || "Aucune suggestion produite");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLlmTesting(false); }
  };

  const activate = async () => {
    try {
      await api.put("/copilot/stt/active", { active: selected, fallbacks: [] });
      setActive(selected);
      toast.success(`${provider?.label} est le moteur actif`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className={`${CARD} flex items-center justify-center gap-3 p-10 text-sm text-slate-400`}>
        <Loader2 size={16} className="animate-spin" /> Chargement des moteurs…
      </div>
    );
  }

  if (!providers.length) {
    return (
      <div className={`${CARD} p-6 text-center text-sm text-slate-500`}>
        Aucun moteur de transcription disponible.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="transcription-settings">
      {/* Sélection du moteur */}
      <div className={`${CARD} p-5`}>
        <div className="flex items-center gap-2 mb-1">
          <Mic size={17} className="text-indigo-600" />
          <h3 className="font-heading text-[15px] font-bold text-slate-900">Transcription temps réel</h3>
        </div>
        <p className="text-[13px] text-slate-500 mb-4">
          Moteur utilisé par le copilote pendant les appels. Il transcrit les deux
          voix séparément pour distinguer le client du commercial.
        </p>

        <div className="flex flex-wrap gap-2">
          {providers.map((p) => {
            const on = selected === p.type;
            const isActive = active === p.type;
            return (
              <button key={p.type} onClick={() => pick(p.type)}
                data-testid={`stt-provider-${p.type}`}
                className={cn("flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-medium",
                  on ? "border-indigo-500 bg-indigo-50/60 text-indigo-700"
                     : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                {p.label}
                {isActive && (
                  <span className={`${PILL} bg-emerald-50 text-emerald-700`}>
                    <span className={`${DOT} bg-emerald-500`} /> actif
                  </span>
                )}
                {p.configuration?.configured && !isActive && (
                  <span className={`${PILL} bg-slate-100 text-slate-500`}>configuré</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Champs générés */}
      {provider && (
        <div className={`${CARD} p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-heading text-[15px] font-bold text-slate-900">
              Configuration · {provider.label}
            </h3>
            {active !== provider.type && provider.configuration?.configured && (
              <button onClick={activate} className={btnGhost} data-testid="stt-activate">
                Définir comme moteur actif
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {provider.config_fields.map((field) => (
              <Field key={field.key} field={field}
                value={form[field.key] ?? field.default ?? ""}
                onChange={(v) => setField(field.key, v)} />
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <button onClick={save} disabled={saving || !dirty} className={btnPrimary}
              data-testid="stt-save">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Enregistrer
            </button>
            <button onClick={test} disabled={testing} className={btnGhost} data-testid="stt-test">
              {testing ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
              Tester la connexion
            </button>
            {dirty && (
              <span className="flex items-center gap-1.5 text-[12.5px] text-amber-700">
                <AlertTriangle size={13} /> Modifications non enregistrées — testez après avoir enregistré.
              </span>
            )}
          </div>

          {result && (
            <div className={cn("mt-4 rounded-xl border p-3.5",
              result.ok ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60")}
              data-testid="stt-test-result">
              <div className="flex items-center gap-2 text-[13.5px] font-medium">
                {result.ok ? <CheckCircle2 size={15} className="text-emerald-600" />
                           : <XCircle size={15} className="text-rose-600" />}
                <span className={result.ok ? "text-emerald-800" : "text-rose-700"}>
                  {result.detail}
                </span>
              </div>
              {result.checks && Object.keys(result.checks).length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {Object.entries(result.checks).map(([key, ok]) => (
                    <span key={key} className={cn(PILL, ok ? "bg-emerald-100 text-emerald-700"
                                                           : "bg-rose-100 text-rose-700")}>
                      <span className={cn(DOT, ok ? "bg-emerald-500" : "bg-rose-500")} />
                      {CHECK_LABELS[key] || key}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Génération des suggestions — l'autre moitié du copilote.
          Transcrire et suggérer sont deux chaînes distinctes (Deepgram d'un
          côté, le modèle de Paramètres › Intégrations de l'autre) : un
          copilote qui affiche la conversation sans jamais rien proposer est
          une panne de CETTE chaîne-là, et elle ne se voyait nulle part. */}
      <div className={`${CARD} p-5`}>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={17} className="text-indigo-600" />
          <h3 className="font-heading text-[15px] font-bold text-slate-900">
            Suggestions du copilote
          </h3>
        </div>
        <p className="text-[13px] text-slate-500 mb-4">
          Le test rejoue une objection type dans les conditions exactes d'un appel
          (même prompt, même playbook, même budget de latence) et paie un vrai
          appel au modèle. C'est le seul moyen de distinguer une clé absente d'un
          modèle refusé ou d'un délai dépassé — sans attendre d'être en ligne avec
          un prospect.
        </p>

        <button onClick={testLlm} disabled={llmTesting} className={btnGhost}
          data-testid="llm-test">
          {llmTesting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          Tester la génération
        </button>

        {llmResult && (
          <div className={cn("mt-4 rounded-xl border p-3.5",
            llmResult.ok ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60")}
            data-testid="llm-test-result">
            <div className="flex items-start gap-2 text-[13.5px] font-medium">
              {llmResult.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" />
                            : <XCircle size={15} className="mt-0.5 shrink-0 text-rose-600" />}
              <span className={llmResult.ok ? "text-emerald-800" : "text-rose-700"}>
                {llmResult.detail}
              </span>
            </div>
            {llmResult.suggestion && (
              <p className="mt-2.5 text-[13px] italic leading-relaxed text-slate-600">
                « {llmResult.suggestion} »
              </p>
            )}
            {llmResult.checks && Object.keys(llmResult.checks).length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {Object.entries(llmResult.checks).map(([key, ok]) => (
                  <span key={key} className={cn(PILL, ok ? "bg-emerald-100 text-emerald-700"
                                                         : "bg-rose-100 text-rose-700")}>
                    <span className={cn(DOT, ok ? "bg-emerald-500" : "bg-rose-500")} />
                    {CHECK_LABELS[key] || key}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rappel de dépendance : sans URL publique en HTTPS, aucun audio ne part */}
      <div className={`${CARD} flex items-start gap-3 p-4`}>
        <HelpCircle size={16} className="mt-0.5 shrink-0 text-slate-400" />
        <p className="text-[12.5px] leading-relaxed text-slate-500">
          La transcription exige que <b className="font-medium text-slate-700">l'URL publique</b> du
          CRM soit en HTTPS (onglet Fournisseur) : le flux audio de l'appel est poussé
          vers le serveur en WebSocket sécurisé. Sans elle, les appels fonctionnent
          normalement mais le copilote reste muet.
        </p>
      </div>
    </div>
  );
}

function Field({ field, value, onChange }) {
  const common = { id: field.key, value, onChange: (e) => onChange(e.target.value) };
  return (
    <div>
      <label htmlFor={field.key} className={FIELD_LABEL}>
        {field.label}{field.required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      {field.type === "select" ? (
        <select {...common} className={`${inputCls} mt-1 bg-white`} data-testid={`stt-field-${field.key}`}>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input {...common} type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
          placeholder={field.placeholder || ""} autoComplete="off"
          data-testid={`stt-field-${field.key}`}
          className={cn(inputCls, "mt-1", field.secret && "font-mono")} />
      )}
      {field.help && <p className="mt-1 text-[11.5px] leading-relaxed text-slate-400">{field.help}</p>}
    </div>
  );
}
